import { Button } from '@shared/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import { Input } from '@shared/components/common/Input';
import yup from '@shared/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@shared/assets/images/email.svg';
import { useStore } from '@shared/hooks/useStore';
import { flowResult } from 'mobx';
import { ROUTES } from '@shared/configs/constants';
import { REGEX_VALIDATE_EMAIL, STEP_ONE } from '@shared/components/constants/common.constants';

export default observer(function InputEmail() {
    // hook
    const navigate = useNavigate();

    // store
    const { authStore: { registerUserByEmail } } = useStore();

    // form
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .required('error.required')
            .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email'),
        confirmEmail: yup
            .string()
            .required('error.required')
            .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email')
            .oneOf([yup.ref('email')], 'error.email_confirm_not_match'),
        companyCode: yup
            .string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
        }
    });

    // state
    const [isSendMailSuccess, setIsSendMailSuccess] = useState(false);

    // function

    const onSubmit = async (data: FormData) => {
        const { email, companyCode } = data;

        const res = await flowResult(registerUserByEmail({ email, companyCode }));

        if (res) {
            setIsSendMailSuccess(true);
        }

        navigate(ROUTES.registrationStepOne.href + '?sub=' + STEP_ONE.INPUT_EMAIL);
    };

    const onError = (error) => {
        console.error(error);
    };

    return (
        <div className='flex flex-col gap-8 w-full'>

            {
                !isSendMailSuccess ?
                    (
                        <>
                            <h2 className='text-center text-cs-sub-title font-bold'>メールアドレス入力</h2>
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit, onError)} className='flex flex-col gap-8'>
                                    <div className='bg-background-1 flex flex-col md:gap-6 gap-4 rounded-[4px]'>
                                        <FormField
                                            control={form.control}
                                            name='email'
                                            render={({ field }) => (
                                                <FormItem className=''>
                                                    <FormLabel className='!mt-0  leading-[25px]' required>
                                                        メールアドレス
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type='text'
                                                            className='h-[56px] rounded-none bg-[#ffffff]'
                                                            {...field}
                                                            onChange={(value) => {
                                                                field.onChange(value);
                                                                form.trigger(['email']);
                                                                if (form.getValues('confirmEmail')?.length > 0) {
                                                                    form.trigger('confirmEmail');
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                    <p className='text-[#525252] text-[0.9375rem]'>※ ログイン時のIDとなります。</p>

                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='confirmEmail'
                                            render={({ field }) => (
                                                <FormItem className=''>
                                                    <FormLabel className='!mt-0  leading-[25px]' required>
                                                        メールアドレスの確認
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type='text'
                                                            className='h-[56px] rounded-none bg-[#ffffff]'
                                                            {...field}
                                                            onPaste={e => e.preventDefault()}
                                                            onChange={(value) => {
                                                                field.onChange(value);
                                                                form.trigger(['email', 'confirmEmail']);
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='companyCode'
                                            render={({ field }) => (
                                                <FormItem className=''>
                                                    <FormLabel className='!mt-0  leading-[25px]'>
                                                        法人コード
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type='text'
                                                            className='h-[56px] rounded-none bg-[#ffffff]'
                                                            {...field}
                                                            onPaste={e => e.preventDefault()}
                                                            onChange={(value) => {
                                                                field.onChange(value);
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='flex gap-4'>
                                        <Button
                                            type='button'
                                            variant='secondary'
                                            onClick={() => navigate(ROUTES.registrationStepOne.href + '?sub=' + STEP_ONE.CONFIRM_TERM)}
                                            className='rounded-[10px] flex-1 h-14 text-base'
                                        >
                                            戻る
                                        </Button>
                                        <Button
                                            type='submit'
                                            variant='default'
                                            className='rounded-[10px] flex-1 h-14 text-base'
                                        >
                                            送信
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </>
                    ) :
                    (
                        <div className='flex flex-col gap-8 w-full'>
                            <div className='bg-background-1 flex flex-col items-center md:gap-6 gap-4 rounded-[4px] text-cs-label font-medium w-full'>
                                <img className='w-[72px]' src={EmailIcon} alt='email-icon' />
                                <div className='space-y-3 text-center'>
                                    <p>メールを送信しました。</p>
                                    <p>送信メール内のURLから会員情報の登録を完了してください</p>
                                </div>

                            </div>
                            <div className='w-full flex justify-center'>
                                <Button onClick={() => navigate('/')} type='submit' variant='default' className='rounded-[10px] w-full h-14'>ホームページに戻る</Button>
                            </div>

                        </div>
                    )
            }

        </div>
    );
});
